import { request } from './client'
import type { StaffAuth } from './teaching'
import type { ClassSummary } from './classes'

/**
 * E3 / E4 — the school's teachers, and which class each one runs.
 *
 * 🔴 FIVE CALLS, AND ONLY TWO OF THEM ARE NEW ROUTES. The feature looked
 * missing, and most of it was already built and reachable by nothing:
 *
 *   `PATCH /classes/:classId`         assign · existed, guarded, uncalled
 *   `POST /teachers/:id/deactivate`   remove  · existed, audited, uncalled
 *   `POST /auth/invite`               re-send · existed, ADMIN-allowed, uncalled
 *   `GET  /school/staff`              list    · NEW — nothing could see a teacher
 *   `POST /school/staff`              create  · NEW — nothing could make one
 *
 * That ratio is the finding. Without the two new ones the other three were
 * unusable: the assignment field had no source of teacher ids, so an admin could
 * not have filled it in even by hand.
 */

/** One row of `GET /school/staff`. */
export interface SchoolStaffRow {
  userId: string
  name: string | null
  email: string | null
  role: string
  isActive: boolean
  /**
   * 🔴 NOT DERIVABLE FROM `isActive`, WHICH IS WHY THE SERVER SENDS IT.
   *
   * An invited account is created deactivated and so is a teacher who has left.
   * Both are `isActive: false` and they need opposite actions — one needs the
   * link re-sent, the other needs leaving alone. An expired invitation is NOT
   * pending: it is a teacher who still cannot sign in and needs a new one.
   */
  invitePending: boolean
  inviteExpiresAt: string | null
  /** Every live class they are assigned to. Empty is a real and common answer. */
  classes: { classId: string; name: string }[]
  /**
   * E4 — every subject they take, and the class they take it in.
   *
   * 🔴 A SEPARATE LIST FROM `classes`, AND THE DIFFERENCE IS THE ONE THIS SCREEN
   * EXISTS TO SHOW. `classes` is what they are RESPONSIBLE for — whose register
   * they keep, whose name is on the report. This is what they TEACH, and the
   * common case is one of the first and several of the second. Folding them
   * together would tell an admin the Maths teacher runs six classes.
   */
  subjects: { classId: string; className: string; subjectId: string; subjectName: string }[]
  /**
   * Children this person can see: a teacher's live classes, or — for a
   * COORDINATOR, who covers every class (S9 "Coordinator · all classes") —
   * the classes they are linked to. Counted on the server, so a coordinator no
   * longer reads 0.
   */
  childCount: number
  /** Set when an admin chose the password (D10 fallback); cleared at their first change. */
  mustChangePassword: boolean
}

/** How a new account gets its first credential (D10). */
export type StaffDelivery = 'INVITE' | 'PASSWORD'

/** What `POST /school/staff` creates. `password` only with `delivery: 'PASSWORD'`. */
export interface NewStaff {
  name: string
  email: string
  role: 'TEACHER' | 'COORDINATOR'
  delivery: StaffDelivery
  password?: string
}

/**
 * The invite that went out, or null for a password account.
 *
 * ⚠️ `emailed: false` MEANS THE MAIL DID NOT LEAVE (mail switched off, or the
 * provider refused). The account exists and the invite is live; the screen
 * offers "Resend" or "Set a password instead". No link is ever returned —
 * it is a credential, and the response body is not a channel for one.
 */
export interface StaffInviteSent {
  emailed: boolean
  expiresAt: string
}

export interface AddedStaff {
  userId: string
  name: string
  email: string
  role: 'TEACHER' | 'COORDINATOR'
  delivery: StaffDelivery
  invite: StaffInviteSent | null
}

/**
 * ✅ NOTHING ONE-TIME IS RETURNED ANY MORE. This used to carry an `inviteToken`
 * the screen had to render as a credential — *"only the token's hash reaches a
 * row, so a second invitation is the only way to help somebody who lost it"*.
 * The admin chooses the password now and already has it, so this is a receipt.
 */
export interface AddedTeacher {
  userId: string
  name: string
  email: string
}

export const getSchoolStaff = (auth: StaffAuth = {}): Promise<SchoolStaffRow[]> =>
  request<SchoolStaffRow[]>('/school/staff', {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/**
 * E3 — create a teacher with the credentials the school will hand them.
 *
 * 🔴 THE PASSWORD IS NOT TRIMMED ANYWHERE ON THE WAY. A leading or trailing
 * space is a legitimate password character, and trimming it in the browser would
 * send the server something other than what the admin typed — after which the
 * teacher could not sign in with the string on their slip of paper. The name and
 * email are trimmed at the call site, because neither has that property.
 */
/**
 * `POST /school/staff` — invite (default) or create with a password.
 *
 * Refusals by `ApiError.code`: `TEACHER_SEATS_FULL` (409 — the seats Kalqy
 * approved are all taken, pending invites included), `EMAIL_TAKEN` (400).
 */
export const addStaff = (body: NewStaff, auth: StaffAuth = {}): Promise<AddedStaff> =>
  request<AddedStaff>('/school/staff', {
    method: 'POST',
    body,
    context: 'user',
    token: auth.token,
  })

/**
 * `POST /school/staff/:userId/invite` — send a fresh invite (the old link
 * stops working). `TEACHER_SEATS_FULL` when the account's earlier invite had
 * expired and no seat is free for it.
 */
export const resendStaffInvite = (userId: string, auth: StaffAuth = {}): Promise<StaffInviteSent> =>
  request<StaffInviteSent>(`/school/staff/${encodeURIComponent(userId)}/invite`, {
    method: 'POST',
    body: {},
    context: 'user',
    token: auth.token,
  })

/** @deprecated Use `addStaff` — kept so older callers compile; creates a TEACHER with a password. */
export const addTeacher = (
  body: { name: string; email: string; password: string },
  auth: StaffAuth = {},
): Promise<AddedTeacher> =>
  request<AddedTeacher>('/school/staff', {
    method: 'POST',
    body,
    context: 'user',
    token: auth.token,
  })

/**
 * E3 — replace a teacher's password.
 *
 * ✅ THIS REPLACES `reissueInvite`, WHICH SENT A FRESH CLAIM LINK. A school that
 * issues its own credentials needs to be able to replace one — `changeOwnPassword`
 * requires the current password, so without this a teacher who forgets theirs
 * has no route back at all.
 *
 * 🔴 IT SIGNS THAT TEACHER OUT EVERYWHERE, and the screen says so before the
 * button. The likeliest reason to use it is that somebody else knows the old
 * password, and leaving their sessions alive would defeat the reset.
 *
 * ⚠️ THE SERVER STILL DECIDES WHO: ADMIN only, TEACHERs only, their own school
 * only, with an audit entry naming who did it.
 */
export const setTeacherPassword = (
  teacherId: string,
  password: string,
  auth: StaffAuth = {},
): Promise<void> =>
  /* ⚠️ NO `expect` OPTION — the route answers 204 and `client.ts` already
     treats an empty body as the successful shape. */
  request<void>(`/school/staff/${teacherId}/password`, {
    method: 'PATCH',
    body: { password },
    context: 'user',
    token: auth.token,
  })

/**
 * E4 — assign a class to a teacher, or clear it.
 *
 * 🔴 `null` IS A REAL INSTRUCTION, NOT AN OMISSION. `updateClassSchema` treats
 * absent and null differently on purpose: `{}` changes nothing, and
 * `teacherId: null` means *unassign*. Sending `undefined` here would silently
 * become "leave it alone", so the caller passes `null` explicitly and this
 * signature refuses to accept `undefined` at all.
 *
 * ⚠️ THE SERVER STILL DECIDES. `resolveTeacher` requires the id to be a real,
 * active TEACHER in the admin's own school and answers one generic refusal for
 * every other case — so a stale dropdown cannot hand a class to a stranger.
 */
export const assignTeacher = (
  classId: string,
  teacherId: string | null,
  auth: StaffAuth = {},
): Promise<ClassSummary> =>
  request<ClassSummary>(`/classes/${classId}`, {
    method: 'PATCH',
    body: { teacherId },
    context: 'user',
    token: auth.token,
  })

/**
 * US-3.2 — *"deactivate a teacher → they can no longer log in, but their classes
 * retain history"*.
 *
 * 🔴 IT DOES NOT UNASSIGN THEM. `Class.teacherId` and every `Session.teacherId`
 * stay, so past sittings still name who ran them — unassigning would be the
 * destructive reading of "deactivate" and would erase the history the
 * requirement exists to protect. The screen has to say so, because "remove" is
 * what an admin will assume the button means.
 */
export const deactivateTeacher = (teacherId: string, auth: StaffAuth = {}): Promise<void> =>
  /* ⚠️ NO `expect` OPTION — the route answers 204 and `client.ts` already
     returns undefined for it: *"202 and 204 carry no body by design; asking for
     JSON would throw on the happy path."* */
  request<void>(`/teachers/${teacherId}/deactivate`, {
    method: 'POST',
    context: 'user',
    token: auth.token,
  })
